import { useEffect, useState } from 'react';
import { toast } from 'sonner';

/**
 * Hook pour gérer l'enregistrement et la mise à jour du Service Worker
 */
export const useServiceWorker = () => {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Vérifier si le Service Worker est supporté
    if (!('serviceWorker' in navigator)) {
      console.log('[SW] Service Workers not supported');
      return;
    }

    // Ne pas enregistrer le SW si on est dans un iframe (ex: simulation admin)
    if (window !== window.top) {
      return;
    }

    // Enregistrer le Service Worker
    const registerSW = async () => {
      try {
        const reg = await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/'
        });
        
        console.log('[SW] Registered successfully:', reg.scope);
        setRegistration(reg);

        // Vérifier les mises à jour
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('[SW] Update available - applying immediately');
                // Passer directement en mode actif pour éviter les apps obsolètes
                newWorker.postMessage('SKIP_WAITING');
                setUpdateAvailable(true);
                toast.info('Mise à jour appliquée', {
                  description: 'Actualisation en cours...',
                  duration: 2000
                });
              }
            });
          }
        });

        // Recharger la page quand le nouveau SW prend le contrôle
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (!refreshing) {
            refreshing = true;
            window.location.reload();
          }
        });

      } catch (error) {
        console.error('[SW] Registration failed:', error);
      }
    };

    registerSW();
  }, []);

  // Fonction pour forcer la mise à jour
  const forceUpdate = () => {
    if (registration && registration.waiting) {
      registration.waiting.postMessage('SKIP_WAITING');
    }
  };

  // Fonction pour vider le cache
  const clearCache = async () => {
    if (registration) {
      registration.active?.postMessage('CLEAR_CACHE');
      toast.success('Cache cleared');
    }
  };

  return {
    registration,
    updateAvailable,
    forceUpdate,
    clearCache
  };
};
